'use client';

/**
 * AgentChatPage - Full-page chat component for embedding in consumer apps
 *
 * Provides a complete chat experience with:
 * - Header with agent info, download, and share buttons
 * - Chat interface (Claude component)
 * - Conversation history panel with date grouping, search, edit, delete
 * - Share and agent details modals
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AssistantRuntimeProvider, type ThreadMessageLike } from '@assistant-ui/react';
import { useEurekaRuntime } from '@/lib/useEurekaRuntime';
import { useConversation, ConversationProvider, type MessageMetadata, type ConversionResult } from '@/lib/useConversation';
import { Claude } from '@/components/claude/Claude';
import type { CatalogItem } from '@/components/assistant-ui/agent-search-dropdown';
import type { ExecutionEvent } from '@/components/assistant-ui/execution-log';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ThemeToggleSimple } from '@/components/ui/theme-toggle';
import { ThemeProvider } from '@/lib/theme-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeftIcon,
  MessageSquareIcon,
  XIcon,
  PlusIcon,
  SendIcon,
  SearchIcon,
  PencilIcon,
  Trash2Icon,
  DownloadIcon,
  Share2Icon,
  InfoIcon,
  CheckIcon,
  LayoutListIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AgentWelcomeInfo, ConversationResponse, PlatformAgentDefinition } from '@/types';
import type { SkillCatalogEntry } from '@/lib/useEurekaRuntime';
import { initAuth, isAuthInitialized } from '@/lib/auth';
import { setApiBaseUrl, setMcpBaseUrl } from '@/lib/apiClient';
import { ExplorerView } from '@/components/assistant-ui/explorer-view';

export interface AgentChatPageProps {
  /** Agent identifier (e.g., "system/explorer" or UUID for DB agents) */
  agentId?: string;
  /** Base path for file-based agents (catalog root directory) */
  basePath?: string;
  /**
   * Platform agent definition for inline agents.
   * When provided, creates a platform conversation with this definition.
   * Takes precedence over agentId for agent execution.
   */
  agentDefinition?: PlatformAgentDefinition;
  /** Display name for the agent */
  agentName: string;
  /** Icon for the agent (emoji or URL) */
  agentIcon?: string;
  /** Short description of the agent */
  agentDescription?: string;
  /** List of agent capabilities */
  agentCapabilities?: string[];
  /** Example prompts for the agent */
  agentExamples?: string[];
  /** Agent version string */
  agentVersion?: string;
  /** Agent configuration for details modal */
  agentConfig?: {
    backend?: string;
    llm?: string;
    reasoning?: string;
    instructions?: string;
  };
  /** MCP servers for agent details modal */
  mcpServers?: Array<{
    name: string;
    type: string;
    connected: boolean;
    tools: string[];
  }>;
  /** Agent tags for determining behavior (e.g., 'router', 'explorer') */
  agentTags?: string[];
  /** MCP servers config for resource binding (name + type format) */
  agentMcpServers?: Array<{ name: string; type: string; timeout?: number }>;
  /** Resource binding mode: 'fixed' (dropdowns) or 'dynamic' (add/remove) */
  agentResourceBindingMode?: 'fixed' | 'dynamic';
  /** Callback when user clicks back/close */
  onBack?: () => void;
  /** Whether to show conversation history panel */
  showHistory?: boolean;
  /** Initial message to send automatically */
  initialMessage?: string;
  /** Default text to pre-fill in the goal input (user must click to send) */
  defaultGoalInput?: string;
  /** Load an existing conversation by ID on mount */
  defaultConversationId?: string;
  /** Callback when a new conversation is created (useful for persisting the ID) */
  onConversationCreated?: (conversationId: string) => void;
  /** Callback when chat is shared */
  onShare?: (emails: string[], conversationId: string) => void;
  /** Callback when chat is downloaded */
  onDownload?: (conversationId: string) => void;
  /** Additional context to pass to agent */
  context?: Record<string, unknown>;
  /** Custom class for the container */
  className?: string;
  /**
   * External token provider function. When provided, this takes precedence
   * over the library's built-in Keycloak authentication.
   */
  getToken?: () => Promise<string | null> | string | null;
  /** Tenant ID for multi-tenancy */
  tenantId?: string;
  /** Project ID for resource scoping */
  projectId?: string;
  /** User ID for tracking */
  userId?: string;
  /** Available agents for "New Chat" modal */
  availableAgents?: AgentInfo[];
  /** Callback when user selects a different agent for new chat */
  onAgentChange?: (agent: AgentInfo) => void;
  /** Enable @ commands for agent search (Explorer only) */
  enableAtCommands?: boolean;
  /** Callback when user navigates to agent via @ command */
  onAgentNavigate?: (agent: any) => void;
  /** Search endpoint for # and @ commands */
  searchEndpoint?: string;
  /** MCP servers configured for this agent */
  configuredMcpServers?: string[];
  /**
   * API base URL for all agent API calls.
   * REQUIRED - must be provided by the consumer app.
   * Example: "/api/agent" (proxied) or "http://localhost:10000" (direct)
   */
  apiBaseUrl: string;
  /**
   * MCP base URL for all MCP server calls.
   * REQUIRED - must be provided by the consumer app.
   * Example: "/api/mcp" (proxied) or "http://localhost:10010" (direct)
   */
  mcpBaseUrl: string;
  /** Whether to show the welcome message when starting a new conversation (default: false) */
  showWelcomeMessage?: boolean;
  /**
   * Skill catalog for Developer Agent.
   * Maps skill_name to { base_path, agent_id } for run_skill tool.
   */
  skillCatalog?: Record<string, SkillCatalogEntry>;
  /**
   * Working directory for agent execution.
   * Used for Developer Agent to run in the context of the selected repo.
   * Subprocess will have cwd set to this path.
   */
  workingDirectory?: string;
  /** Environment ID for per-environment bindings in runtime_binding.yaml */
  environmentId?: string;
  /** Developer persona for skill filtering (platform or agent) */
  persona?: 'platform' | 'agent';
  /**
   * Force show the agent list sidebar regardless of agent type.
   * When true, the agent list is always shown (user can still toggle).
   * When false/undefined, it depends on agent tags (router/explorer).
   */
  forceShowAgentList?: boolean;
  /**
   * Custom render function for the empty state (when no conversation is selected).
   * When provided, this replaces the default "No conversation selected" message.
   * Useful for agent-specific welcome pages (e.g., Developer Agent).
   */
  renderEmptyState?: (props: { onNewChat: () => void; onStartConversation: (message: string) => void }) => React.ReactNode;
  /**
   * Additional configuration to pass to agent execution.
   * This is merged with other configuration and passed to the executor.
   * Used for passing agent_context when opening AI Mode from agent details.
   */
  configuration?: Record<string, unknown>;
}

// ============================================================================
// Date Grouping Utilities
// ============================================================================

type DateGroup = 'Today' | 'Yesterday' | 'Last 7 days' | 'Last 30 days' | 'Older';

function getDateGroup(date: Date): DateGroup {
  const now = new Date();
  const messageDate = new Date(date);

  // Reset time to midnight for accurate day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());

  const diffTime = today.getTime() - msgDay.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 7) return 'Last 7 days';
  if (diffDays <= 30) return 'Last 30 days';
  return 'Older';
}

function groupConversationsByDate(conversations: ConversationResponse[]): Record<DateGroup, ConversationResponse[]> {
  const groups: Record<DateGroup, ConversationResponse[]> = {
    'Today': [],
    'Yesterday': [],
    'Last 7 days': [],
    'Last 30 days': [],
    'Older': [],
  };

  conversations.forEach(conv => {
    const date = new Date(conv.updated_at || conv.created_at);
    const group = getDateGroup(date);
    groups[group].push(conv);
  });

  return groups;
}


// ============================================================================
// Agent Info Type (for agent selector)
// ============================================================================

export interface AgentInfo {
  /** Agent identifier (e.g., "system/explorer" or UUID for DB agents) */
  agent_id: string;
  /** Execution ID - the actual agent to call (e.g., 'system/helper' for user agents) */
  execution_id?: string;
  /** Display name for the agent */
  name: string;
  /** Agent category (e.g., "codify", "explore", "data") */
  category?: string;
  /** Icon emoji or URL */
  icon?: string;
  /** Short description for display */
  short_description?: string;
  /** Long description for details */
  long_description?: string;
  /** Example usage prompts */
  usage_examples?: string[] | null;
  /** Agent capabilities */
  capabilities?: string[] | null;
  /** Available tools */
  tools?: string[];
  /** Version string */
  version?: string;
  /** Author name */
  author?: string | null;
  /** Tags for filtering */
  tags?: string[];
  /** Whether agent is enabled */
  enabled?: boolean;
  /** Base path for file-based agents (catalog root directory) */
  basePath?: string;

  // Legacy fields for backward compatibility
  /** @deprecated Use agent_id instead */
  id?: string | number;
  /** @deprecated Use short_description instead */
  description?: string;
  /** Agent config (for custom agents) */
  config?: {
    backend?: string;
    llm?: string;
    reasoning?: string;
    instructions?: string;
  };
  /** MCP servers (for custom agents) */
  mcpServers?: Array<{
    name: string;
    type: string;
    connected: boolean;
    tools: string[];
  }>;
}

// ============================================================================
// New Chat Modal (Agent Selector)
// ============================================================================

interface NewChatModalProps {
  open: boolean;
  onClose: () => void;
  onSelectAgent: (agent: AgentInfo) => void;
  agents: AgentInfo[];
}

function NewChatModal({ open, onClose, onSelectAgent, agents }: NewChatModalProps) {
  const handleSelect = (agent: AgentInfo) => {
    onSelectAgent(agent);
    onClose();
  };

  // Get agent ID (prefer agent_id, fallback to id)
  const getAgentKey = (agent: AgentInfo) => agent.agent_id || agent.id || agent.name;

  // Get description (prefer short_description, fallback to description)
  const getDescription = (agent: AgentInfo) => agent.short_description || agent.description;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Select an Agent</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-4 max-h-[400px] overflow-auto">
          {agents.map((agent) => (
            <button
              key={getAgentKey(agent)}
              onClick={() => handleSelect(agent)}
              className="w-full text-left p-4 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {agent.icon && <span className="text-base">{agent.icon}</span>}
                  <span className="font-medium">{agent.name}</span>
                </div>
                {agent.version && (
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                    {agent.version}
                  </span>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5 flex-wrap">
                {/* Category badge */}
                {agent.category && (
                  <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded text-[10px] uppercase">
                    {agent.category}
                  </span>
                )}
                {/* Tools count */}
                {agent.tools && agent.tools.length > 0 && (
                  <>
                    <span className="opacity-50">•</span>
                    <span>{agent.tools.length} tool(s)</span>
                  </>
                )}
                {/* Legacy: MCP servers */}
                {agent.mcpServers && agent.mcpServers.length > 0 && (
                  <>
                    <span className="opacity-50">•</span>
                    <span>{agent.mcpServers.length} MCP server(s)</span>
                  </>
                )}
                {/* Legacy: Config info */}
                {agent.config?.backend && (
                  <>
                    <span className="opacity-50">•</span>
                    <span>{agent.config.backend}</span>
                  </>
                )}
                {agent.config?.llm && (
                  <>
                    <span className="opacity-50">•</span>
                    <span>{agent.config.llm}</span>
                  </>
                )}
              </div>
              {/* Short description */}
              {getDescription(agent) && (
                <div className="text-xs text-muted-foreground mt-2 line-clamp-2">
                  {getDescription(agent)}
                </div>
              )}
            </button>
          ))}
          {agents.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <span className="text-sm">No agents available</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Share Chat Modal
// ============================================================================

interface ShareChatModalProps {
  open: boolean;
  onClose: () => void;
  onShare: (emails: string[]) => void;
  conversationTitle?: string;
}

function ShareChatModal({ open, onClose, onShare, conversationTitle }: ShareChatModalProps) {
  const [emails, setEmails] = useState<string[]>(['']);

  const addEmailInput = () => {
    setEmails([...emails, '']);
  };

  const removeEmailInput = (index: number) => {
    if (emails.length > 1) {
      setEmails(emails.filter((_, i) => i !== index));
    }
  };

  const updateEmail = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const handleShare = () => {
    const validEmails = emails.filter(e => e.trim() && e.includes('@'));
    if (validEmails.length > 0) {
      onShare(validEmails);
      setEmails(['']);
      onClose();
    }
  };

  const handleClose = () => {
    setEmails(['']);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share Chat</DialogTitle>
          <DialogDescription>
            Share "{conversationTitle || 'this conversation'}" with others via email.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-4">
          {emails.map((email, index) => (
            <div key={index} className="flex gap-2">
              <Input
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => updateEmail(index, e.target.value)}
                className="flex-1"
              />
              {emails.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeEmailInput(index)}
                  className="h-10 w-10 p-0 text-muted-foreground hover:text-destructive"
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          <button
            onClick={addEmailInput}
            className="text-sm text-primary hover:underline"
          >
            + Add another email
          </button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleShare}>
            Share
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Agent Details Modal
// ============================================================================

interface AgentDetailsModalProps {
  open: boolean;
  onClose: () => void;
  agentName: string;
  agentVersion?: string;
  agentConfig?: {
    backend?: string;
    llm?: string;
    reasoning?: string;
    instructions?: string;
  };
  mcpServers?: Array<{
    name: string;
    type: string;
    connected: boolean;
    tools: string[];
  }>;
}

function AgentDetailsModal({
  open,
  onClose,
  agentName,
  agentVersion,
  agentConfig,
  mcpServers,
}: AgentDetailsModalProps) {
  const backendNames: Record<string, string> = {
    'openai': 'OpenAI Agent',
    'eureka-v0.1': 'Eureka Agent (v0.1)',
    'eureka-v0.2': 'Eureka Agent (v0.2)',
  };

  const llmNames: Record<string, string> = {
    'gpt5': 'GPT-5',
    'gpt5.1': 'GPT-5.1',
    'claude-4.5-sonnet': 'Claude 4.5 Sonnet',
    'claude-4.5-haiku': 'Claude 4.5 Haiku',
    'claude-4.5-opus': 'Claude 4.5 Opus',
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {agentName}
            {agentVersion && (
              <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded">
                {agentVersion}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Configuration */}
          {agentConfig && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Configuration
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {agentConfig.backend && (
                  <div>
                    <span className="text-muted-foreground">Backend:</span>
                    <span className="ml-2 font-medium">
                      {backendNames[agentConfig.backend] || agentConfig.backend}
                    </span>
                  </div>
                )}
                {agentConfig.llm && (
                  <div>
                    <span className="text-muted-foreground">LLM:</span>
                    <span className="ml-2 font-medium">
                      {llmNames[agentConfig.llm] || agentConfig.llm}
                    </span>
                  </div>
                )}
                {agentConfig.reasoning && (
                  <div>
                    <span className="text-muted-foreground">Reasoning:</span>
                    <span className="ml-2 font-medium capitalize">{agentConfig.reasoning}</span>
                  </div>
                )}
              </div>
              {agentConfig.instructions && (
                <div className="mt-2">
                  <span className="text-sm text-muted-foreground">Instructions:</span>
                  <p className="mt-1 text-sm bg-muted/50 p-2 rounded">
                    {agentConfig.instructions}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* MCP Servers */}
          {mcpServers && mcpServers.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                MCP Servers ({mcpServers.length})
              </h4>
              <div className="space-y-3">
                {mcpServers.map((server, index) => (
                  <div key={index} className="bg-muted/30 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "h-2 w-2 rounded-full",
                          server.connected ? "bg-green-500" : "bg-red-500"
                        )}
                      />
                      <span className="font-medium">{server.name}</span>
                      <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {server.type}
                      </span>
                    </div>
                    {server.tools.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {server.tools.map((tool, toolIndex) => (
                          <span
                            key={toolIndex}
                            className="text-xs bg-background border border-border px-2 py-0.5 rounded font-mono"
                          >
                            {tool}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Delete Confirmation Modal
// ============================================================================

interface DeleteConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  conversationTitle?: string;
}

function DeleteConfirmModal({ open, onClose, onConfirm, conversationTitle }: DeleteConfirmModalProps) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Conversation</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete "{conversationTitle || 'this conversation'}"? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Conversation Item Component
// ============================================================================

interface ConversationItemProps {
  conversation: ConversationResponse;
  isActive: boolean;
  onSelect: () => void;
  onEdit: (newTitle: string) => void;
  onDelete: () => void;
  agentName?: string;
  agentVersion?: string;
}

function ConversationItem({
  conversation,
  isActive,
  onSelect,
  onEdit,
  onDelete,
  agentName,
  agentVersion,
}: ConversationItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(conversation.title || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEditStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditTitle(conversation.title || '');
    setIsEditing(true);
  };

  const handleEditSave = () => {
    const newTitle = editTitle.trim();
    if (newTitle && newTitle !== conversation.title) {
      onEdit(newTitle);
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditTitle(conversation.title || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEditCancel();
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <div
      onClick={onSelect}
      className={cn(
        "group relative rounded-lg border p-3 cursor-pointer transition-all",
        isActive
          ? "bg-primary/10 border-primary/30"
          : "bg-card border-border hover:border-primary/20 hover:bg-muted/50"
      )}
    >
      {/* Title row */}
      <div className="flex items-start gap-2">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={handleEditSave}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 text-sm font-medium bg-background border border-border rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        ) : (
          <span className="flex-1 text-sm font-medium truncate">
            {conversation.title || 'Untitled'}
          </span>
        )}

        {/* Action buttons - show on hover */}
        {!isEditing && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={handleEditStart}
              className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
              title="Edit title"
            >
              <PencilIcon className="h-3 w-3" />
            </button>
            <button
              onClick={handleDeleteClick}
              className="p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-colors"
              title="Delete conversation"
            >
              <Trash2Icon className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      {/* Agent info */}
      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-xs text-muted-foreground truncate">
          {agentName || (typeof conversation.metadata?.agent_name === 'string' ? conversation.metadata.agent_name : 'Agent')}
        </span>
        {agentVersion && (
          <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
            {agentVersion}
          </span>
        )}
      </div>

      {/* Stats row - tokens, turns, date */}
      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
        {conversation.execution_count !== undefined && conversation.execution_count > 0 && (
          <span className="flex items-center gap-0.5" title="Turns">
            <span className="opacity-60">⟳</span>
            {conversation.execution_count}
          </span>
        )}
        {conversation.total_tokens !== undefined && conversation.total_tokens > 0 && (
          <span className="flex items-center gap-0.5" title="Tokens">
            <span className="opacity-60">◈</span>
            {conversation.total_tokens >= 1000
              ? `${(conversation.total_tokens / 1000).toFixed(1)}k`
              : conversation.total_tokens}
          </span>
        )}
        {conversation.total_cost !== undefined && conversation.total_cost > 0 && (
          <span className="flex items-center gap-0.5" title="Cost">
            <span className="opacity-60">$</span>
            {conversation.total_cost.toFixed(3)}
          </span>
        )}
        <span className="ml-auto opacity-60">
          {new Date(conversation.updated_at || conversation.created_at).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// History Panel Component
// ============================================================================

interface HistoryPanelProps {
  conversations: ConversationResponse[];
  activeConversationId: string | null;
  showLandingPage: boolean;
  onSelectConversation: (convId: string) => void;
  onNewChat: () => void;
  onEditTitle: (convId: string, newTitle: string) => void;
  onDeleteConversation: (convId: string, title: string) => void;
  agentName?: string;
  agentVersion?: string;
  onClose: () => void;
}

function HistoryPanel({
  conversations,
  activeConversationId,
  showLandingPage,
  onSelectConversation,
  onNewChat,
  onEditTitle,
  onDeleteConversation,
  agentName,
  agentVersion,
  onClose,
}: HistoryPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState<{ convId: string; title: string } | null>(null);

  // Filter conversations by search
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const query = searchQuery.toLowerCase();
    return conversations.filter(conv => {
      const title = (conv.title || '').toLowerCase();
      return title.includes(query);
    });
  }, [conversations, searchQuery]);

  // Group by date
  const groupedConversations = useMemo(() => {
    return groupConversationsByDate(filteredConversations);
  }, [filteredConversations]);

  const handleDelete = (convId: string, title: string) => {
    setDeleteModal({ convId, title });
  };

  const confirmDelete = () => {
    if (deleteModal) {
      onDeleteConversation(deleteModal.convId, deleteModal.title);
      setDeleteModal(null);
    }
  };

  const dateGroups: DateGroup[] = ['Today', 'Yesterday', 'Last 7 days', 'Last 30 days', 'Older'];

  return (
    <>
      <div className="w-72 shrink-0 border-r border-border bg-card flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-border">
          <span className="text-sm font-medium">Conversations</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-6 w-6 p-0"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>

        {/* Search */}
        <div className="p-2 border-b border-border">
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-auto p-2">
          {filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              {searchQuery ? (
                <>
                  <SearchIcon className="h-8 w-8 mb-2 opacity-50" />
                  <span className="text-sm">No results found</span>
                </>
              ) : (
                <>
                  <MessageSquareIcon className="h-8 w-8 mb-2 opacity-50" />
                  <span className="text-sm">No conversations yet</span>
                  <span className="text-xs mt-1">Start a new chat to begin</span>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {dateGroups.map(group => {
                const convs = groupedConversations[group];
                if (convs.length === 0) return null;

                return (
                  <div key={group}>
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
                      {group}
                    </div>
                    <div className="space-y-2">
                      {convs.map(conv => (
                        <ConversationItem
                          key={conv.conversation_id}
                          conversation={conv}
                          isActive={conv.conversation_id === activeConversationId && !showLandingPage}
                          onSelect={() => onSelectConversation(conv.conversation_id)}
                          onEdit={(newTitle) => onEditTitle(conv.conversation_id, newTitle)}
                          onDelete={() => handleDelete(conv.conversation_id, conv.title || 'Untitled')}
                          agentName={agentName}
                          agentVersion={agentVersion}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        open={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        onConfirm={confirmDelete}
        conversationTitle={deleteModal?.title}
      />
    </>
  );
}

// ============================================================================
// Chat Header Component (inside chat area)
// ============================================================================

interface ChatHeaderProps {
  conversation: ConversationResponse | undefined;
  agentName: string;
  agentVersion?: string;
  onEditTitle: (newTitle: string) => void;
  onViewDetails: () => void;
}

function ChatHeader({
  conversation,
  agentName,
  agentVersion,
  onEditTitle,
  onViewDetails,
}: ChatHeaderProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(conversation?.title || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditTitle(conversation?.title || '');
  }, [conversation?.title]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEditStart = () => {
    setEditTitle(conversation?.title || '');
    setIsEditing(true);
  };

  const handleEditSave = () => {
    const newTitle = editTitle.trim();
    if (newTitle && newTitle !== conversation?.title) {
      onEditTitle(newTitle);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      setEditTitle(conversation?.title || '');
      setIsEditing(false);
    }
  };

  if (!conversation) return null;

  return (
    <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card/50">
      <div className="flex items-center gap-3">
        {/* Title with edit */}
        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input
                ref={inputRef}
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onBlur={handleEditSave}
                onKeyDown={handleKeyDown}
                className="text-sm font-medium bg-background border border-border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[200px]"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEditSave}
                className="h-7 w-7 p-0"
              >
                <CheckIcon className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <>
              <span className="text-sm font-medium">{conversation.title || 'Untitled'}</span>
              <button
                onClick={handleEditStart}
                className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
                title="Edit title"
              >
                <PencilIcon className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>

        {/* Agent info */}
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="text-xs">{agentName}</span>
          {agentVersion && (
            <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded">
              {agentVersion}
            </span>
          )}
          <button
            onClick={onViewDetails}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <InfoIcon className="h-3 w-3" />
            Details
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Chat Runtime Component
// ============================================================================

interface ChatRuntimeProps {
  conversationId: string | null;
  initialMessages: ThreadMessageLike[];
  initialMetadata: Map<string, MessageMetadata>;
  initialEvents: ExecutionEvent[];
  fetchMessages: (convId: string) => Promise<ConversionResult>;
  ensureConversation: (goal?: string) => Promise<string>;
  onExecutionStarted: (executionId: string, agentType: string) => void;
  tenantId: string;
  projectId: string;
  userId: string;
  agentId: string;
  /** Base path for file-based agents (catalog root directory) */
  basePath?: string;
  /** Skill catalog for Developer Agent (maps skill_name to { base_path, agent_id }) */
  skillCatalog?: Record<string, SkillCatalogEntry>;
  /** Working directory for agent execution (e.g., selected repo) */
  workingDirectory?: string;
  /** Environment ID for per-environment bindings */
  environmentId?: string;
  /** Developer persona for skill filtering (platform or agent) */
  persona?: 'platform' | 'agent';
  /** Additional configuration to pass to agent execution */
  configuration?: Record<string, unknown>;
  initialMessage?: string;
  /** Enable @ commands for catalog search */
  enableAtCommands?: boolean;
  /** Available agents/skills for @ search */
  availableAgents?: AgentInfo[];
}

const ChatRuntime = ({
  conversationId,
  initialMessages,
  initialMetadata,
  initialEvents,
  fetchMessages,
  ensureConversation,
  onExecutionStarted,
  tenantId,
  projectId,
  userId,
  agentId,
  basePath,
  skillCatalog,
  workingDirectory,
  environmentId,
  persona,
  configuration,
  initialMessage,
  enableAtCommands,
  availableAgents,
}: ChatRuntimeProps) => {
  // Debug logging for file-based configuration
  console.log('🔍 [ChatRuntime] Mounted/rendered with:', {
    conversationId,
    agentId,
    basePath,
    hasSkillCatalog: !!skillCatalog,
    skillCatalogKeys: skillCatalog ? Object.keys(skillCatalog) : [],
    persona,
  });

  const {
    runtime,
    executionEvents,
    isRunning,
    isConnected,
    waitingForUserInput,
    submitUserResponse,
    sendMessage,
    reconnectToExecution,
    setMessages,
    syncEvents,
    cancelExecution,
  } = useEurekaRuntime({
    conversationId,
    ensureConversation,
    onExecutionStarted,
    tenantId,
    projectId,
    userId,
    defaultAgentId: agentId,
    basePath,
    skillCatalog,
    workingDirectory,
    environmentId,
    persona,
    configuration,
    initialMessages,
    initialEvents,
  });

  const sentInitialMessageRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (initialMessage && sentInitialMessageRef.current !== initialMessage) {
      sentInitialMessageRef.current = initialMessage;
      setTimeout(() => {
        sendMessage(initialMessage);
      }, 100);
    }
  }, [initialMessage, sendMessage]);

  const handleManualRefresh = useCallback(async () => {
    if (!conversationId) return;

    const result = await fetchMessages(conversationId);
    setMessages(result.messages);
    syncEvents(result.events);

    if (result.isLastExecutionActive) {
      reconnectToExecution(conversationId);
    }
  }, [conversationId, fetchMessages, setMessages, syncEvents, reconnectToExecution]);

  // Build searchCatalog from skillCatalog (preferred) or availableAgents for @ command support
  const searchCatalog: CatalogItem[] = useMemo(() => {
    // For Developer Assistant, use skillCatalog (skills are the @ commands)
    if (skillCatalog && Object.keys(skillCatalog).length > 0) {
      return Object.entries(skillCatalog).map(([skillName, skillInfo]) => ({
        id: skillName,
        name: skillName,
        displayName: skillName,
        description: `Skill: ${skillName}`,
        type: 'skill' as const,
      }));
    }
    // Fallback to availableAgents for non-developer modes
    if (!availableAgents) return [];
    return availableAgents.map(agent => ({
      id: agent.agent_id,
      name: agent.name,
      displayName: agent.name,
      description: agent.short_description || agent.long_description,
      category: agent.category,
      tags: agent.tags,
      icon: agent.icon,
      type: 'agent' as const,
    }));
  }, [skillCatalog, availableAgents]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <Claude
        executionEvents={executionEvents}
        isRunning={isRunning}
        isConnected={isConnected}
        messageMetadata={initialMetadata}
        waitingForUserInput={waitingForUserInput}
        onSubmitUserResponse={submitUserResponse}
        onSendMessage={sendMessage}
        onReconnect={handleManualRefresh}
        onCancel={cancelExecution}
        enableAtCommands={enableAtCommands}
        searchCatalog={searchCatalog}
      />
    </AssistantRuntimeProvider>
  );
};

// ============================================================================
// Chat View Component
// ============================================================================

interface ChatViewProps {
  conversationId: string | null;
  fetchMessages: (convId: string) => Promise<ConversionResult>;
  ensureConversation: (goal?: string) => Promise<string>;
  onExecutionStarted: (executionId: string, agentType: string) => void;
  tenantId: string;
  projectId: string;
  userId: string;
  agentId: string;
  /** Base path for file-based agents (catalog root directory) */
  basePath?: string;
  /** Skill catalog for Developer Agent */
  skillCatalog?: Record<string, SkillCatalogEntry>;
  /** Working directory for agent execution (e.g., selected repo) */
  workingDirectory?: string;
  /** Environment ID for per-environment bindings */
  environmentId?: string;
  /** Developer persona for skill filtering (platform or agent) */
  persona?: 'platform' | 'agent';
  /** Additional configuration to pass to agent execution */
  configuration?: Record<string, unknown>;
  agentWelcomeInfo?: AgentWelcomeInfo | null;
  initialMessage?: string;
  /** Enable @ commands for catalog search */
  enableAtCommands?: boolean;
  /** Available agents/skills for @ search */
  availableAgents?: AgentInfo[];
}

const ChatView = ({
  conversationId,
  fetchMessages,
  ensureConversation,
  onExecutionStarted,
  tenantId,
  projectId,
  userId,
  agentId,
  basePath,
  skillCatalog,
  workingDirectory,
  environmentId,
  persona,
  configuration,
  agentWelcomeInfo,
  initialMessage,
  enableAtCommands,
  availableAgents,
}: ChatViewProps) => {
  const [initialMessages, setInitialMessages] = useState<ThreadMessageLike[] | null>(null);
  const [messageMetadata, setMessageMetadata] = useState<Map<string, MessageMetadata>>(new Map());
  const [initialEvents, setInitialEvents] = useState<ExecutionEvent[]>([]);

  useEffect(() => {
    if (conversationId) {
      fetchMessages(conversationId).then(result => {
        setInitialMessages(result.messages);
        setMessageMetadata(result.metadata);
        setInitialEvents(result.events);
      });
    } else {
      setInitialMessages([]);
      setMessageMetadata(new Map());
      setInitialEvents([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (initialMessages === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <ChatRuntime
      conversationId={conversationId}
      initialMessages={initialMessages}
      initialMetadata={messageMetadata}
      initialEvents={initialEvents}
      fetchMessages={fetchMessages}
      ensureConversation={ensureConversation}
      onExecutionStarted={onExecutionStarted}
      tenantId={tenantId}
      projectId={projectId}
      userId={userId}
      agentId={agentId}
      basePath={basePath}
      skillCatalog={skillCatalog}
      workingDirectory={workingDirectory}
      environmentId={environmentId}
      persona={persona}
      configuration={configuration}
      initialMessage={initialMessage}
      enableAtCommands={enableAtCommands}
      availableAgents={availableAgents}
    />
  );
};

// ============================================================================
// Empty State Component
// ============================================================================

interface EmptyStateProps {
  onNewChat: () => void;
}

function EmptyState({ onNewChat }: EmptyStateProps) {
  return (
    <div className="flex h-full flex-col items-center justify-center p-6">
      <div className="text-6xl mb-4">💬</div>
      <h2 className="text-lg font-medium mb-2">No conversation selected</h2>
      <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
        Select a conversation from the sidebar to continue
      </p>
    </div>
  );
}

// ============================================================================
// Agent Catalog Panel - standalone sidebar for agent selection
// ============================================================================

interface AgentCatalogPanelProps {
  agents: AgentInfo[];
  selectedAgentId: string;
  onSelectAgent: (agent: AgentInfo) => void;
}

const AgentCatalogPanel = ({ agents, selectedAgentId, onSelectAgent }: AgentCatalogPanelProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) return agents;
    const q = searchQuery.toLowerCase();
    return agents.filter(a =>
      (a.name || '').toLowerCase().includes(q) ||
      (a.short_description || '').toLowerCase().includes(q) ||
      (a.category || '').toLowerCase().includes(q)
    );
  }, [agents, searchQuery]);

  return (
    <div className="w-72 shrink-0 border-r border-border bg-card overflow-hidden flex flex-col">
      <div className="p-3 border-b border-border">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {filteredAgents.map((agent) => (
          <button
            key={agent.agent_id}
            onClick={() => onSelectAgent(agent)}
            className={cn(
              "w-full text-left rounded-lg border p-3 transition-all",
              selectedAgentId === agent.agent_id
                ? "border-primary bg-primary/5"
                : "border-border bg-background hover:border-primary/50 hover:bg-muted/50"
            )}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{agent.icon || "\u{1F916}"}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-foreground truncate">
                    {agent.name}
                  </span>
                  {agent.version && (
                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                      {agent.version}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {agent.short_description}
                </p>
              </div>
              <ChevronRightIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </div>
          </button>
        ))}
        {filteredAgents.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">No agents found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Content Component
// ============================================================================

const AgentChatContent = ({
  agentId,
  basePath,
  agentDefinition,
  agentName,
  agentIcon,
  agentDescription,
  agentCapabilities,
  agentExamples,
  agentVersion,
  agentConfig,
  mcpServers,
  agentTags,
  agentMcpServers,
  agentResourceBindingMode,
  onBack,
  showHistory = true,
  initialMessage,
  defaultGoalInput,
  defaultConversationId,
  onConversationCreated,
  onShare,
  onDownload,
  className,
  getToken,
  availableAgents,
  onAgentChange,
  enableAtCommands,
  onAgentNavigate,
  searchEndpoint,
  configuredMcpServers,
  apiBaseUrl,
  mcpBaseUrl,
  showWelcomeMessage = false,
  skillCatalog,
  workingDirectory,
  environmentId,
  persona,
  forceShowAgentList,
  renderEmptyState,
  configuration,
}: AgentChatPageProps) => {
  // DEBUG: Log skillCatalog to understand why @ dropdown shows wrong items
  console.log('🎯 [AgentChatContent] skillCatalog prop:', skillCatalog ? Object.keys(skillCatalog) : null);
  console.log('🎯 [AgentChatContent] availableAgents count:', availableAgents?.length || 0);

  // Configure API base URL - REQUIRED, must be done before any API calls
  useEffect(() => {
    setApiBaseUrl(apiBaseUrl);
  }, [apiBaseUrl]);

  // Configure MCP base URL - REQUIRED, must be done before any MCP calls
  useEffect(() => {
    setMcpBaseUrl(mcpBaseUrl);
  }, [mcpBaseUrl]);

  // Initialize auth when getToken prop is provided
  useEffect(() => {
    if (getToken && !isAuthInitialized()) {
      initAuth({
        mode: 'external',
        tokenProvider: getToken,
      });
    }
  }, [getToken]);

  const {
    conversationId,
    ensureConversation,
    addExecution,
    startNewConversation,
    switchToConversation,
    fetchMessages,
    conversations,
    loadConversations,
    updateConversationTitle,
    deleteConversation,
    clearConversation,
    tenantId,
    projectId,
    userId,
    executionCount,
  } = useConversation();

  // Unified sidebar: only one panel visible at a time (agents or history)
  const [activePanel, setActivePanel] = useState<'agents' | 'history' | null>('history');
  // Skip landing page when there's an initial message or existing conversation
  // Note: basePath alone should NOT skip landing page - users still need to enter a goal
  const [showLandingPage, setShowLandingPage] = useState(!initialMessage && !defaultConversationId);
  const [goalInput, setGoalInput] = useState(defaultGoalInput || '');
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollTop = useRef(0);
  const [pendingInitialMessage, setPendingInitialMessage] = useState<string | undefined>(undefined);
  // Platform agent definition ID - set when platform conversation is created
  const [platformAgentId, setPlatformAgentId] = useState<string | undefined>(undefined);

  // Effective agent ID: platform agent ID (from platform conversation) takes precedence
  const effectiveAgentId = platformAgentId || agentId || '';

  // Determine if this is a router/explorer agent (shows agent picker)
  const isRouterAgent = useMemo(() => {
    // Check agent tags for 'router' or 'explorer'
    if (agentTags?.some(tag => ['router', 'explorer', 'finder'].includes(tag.toLowerCase()))) {
      return true;
    }
    // Also check agent ID for common router patterns
    const routerIds = ['system/explorer', 'system/finder', 'system/router', 'explorer', 'finder', 'router'];
    return effectiveAgentId ? routerIds.includes(effectiveAgentId.toLowerCase()) : false;
  }, [agentTags, effectiveAgentId]);

  // Modal states
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [agentDetailsModalOpen, setAgentDetailsModalOpen] = useState(false);
  const [newChatModalOpen, setNewChatModalOpen] = useState(false);

  // Get current conversation
  const currentConversation = useMemo(() => {
    return conversations.find(c => c.conversation_id === conversationId);
  }, [conversations, conversationId]);

  // Auto-hide header on scroll
  const handleScroll = useCallback((e: Event) => {
    const target = e.target as HTMLElement;
    const currentScrollTop = target.scrollTop;
    const scrollThreshold = 50;

    if (currentScrollTop > scrollThreshold) {
      if (currentScrollTop > lastScrollTop.current + 10) {
        setHeaderVisible(false);
      } else if (currentScrollTop < lastScrollTop.current - 10) {
        setHeaderVisible(true);
      }
    } else {
      setHeaderVisible(true);
    }

    lastScrollTop.current = currentScrollTop;
  }, []);

  useEffect(() => {
    const chatViewport = document.querySelector('[data-radix-scroll-area-viewport], .overflow-y-scroll');
    if (chatViewport) {
      chatViewport.addEventListener('scroll', handleScroll);
      return () => chatViewport.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll, showLandingPage]);

  // Create welcome info from props
  const agentWelcomeInfo: AgentWelcomeInfo = {
    name: agentName,
    icon: agentIcon,
    short_description: agentDescription || '',
    long_description: agentDescription,
    capabilities: agentCapabilities,
    usage_examples: agentExamples,
  };

  // Load conversations on mount
  useEffect(() => {
    loadConversations(undefined, agentId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  // Load default conversation on mount if provided
  useEffect(() => {
    if (defaultConversationId && !conversationId) {
      switchToConversation(defaultConversationId).then((conv) => {
        if (conv) {
          setShowLandingPage(false);
        } else {
          setShowLandingPage(true);
        }
      }).catch(() => {
        setShowLandingPage(true);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultConversationId]);

  // Auto-start conversation when initialMessage is provided
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (initialMessage && !defaultConversationId && !conversationId && !autoStartedRef.current && tenantId) {
      autoStartedRef.current = true;
      setPendingInitialMessage(initialMessage);
      startNewConversation({
        title: initialMessage.slice(0, 50) + (initialMessage.length > 50 ? '...' : ''),
        mode: 'explore',
        agentType: agentId,  // Pass agentId as agentType for conversation filtering
        agentName,
        agentDefinition,  // Pass platform agent definition if provided
      }).then((response) => {
        if (onConversationCreated && response?.conversation_id) {
          onConversationCreated(response.conversation_id);
        }
        // For platform conversations, store the agent_definition_id for subsequent messages
        if ('agent_definition_id' in response && response.agent_definition_id) {
          setPlatformAgentId(response.agent_definition_id);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage, defaultConversationId, conversationId, tenantId]);

  // Note: basePath alone no longer skips landing page - users need to enter a goal
  // Landing page is only skipped when initialMessage is provided (e.g., from Dev Center "Run" with goal)

  // Handle starting a new conversation from landing page
  const handleStartNewConversation = useCallback(async (initialGoal?: string) => {
    const goal = (initialGoal ?? goalInput).trim() || undefined;
    setPendingInitialMessage(goal);
    const response = await startNewConversation({
      title: goal || agentName,
      goal,  // Pass goal for platform conversations
      mode: 'explore',
      agentType: agentId,  // Pass agentId as agentType for conversation filtering
      agentName,
      agentDefinition,  // Pass platform agent definition if provided
    });
    if (onConversationCreated && response?.conversation_id) {
      onConversationCreated(response.conversation_id);
    }
    // For platform conversations, store the agent_definition_id for subsequent messages
    if ('agent_definition_id' in response && response.agent_definition_id) {
      setPlatformAgentId(response.agent_definition_id);
    }
    setShowLandingPage(false);
  }, [goalInput, agentName, agentId, agentDefinition, startNewConversation, onConversationCreated]);

  // Handle continuing an existing conversation
  const handleContinueConversation = useCallback((convId: string) => {
    switchToConversation(convId);
    setShowLandingPage(false);
    setPendingInitialMessage(undefined);
  }, [switchToConversation]);

  // Handle new chat from history panel
  const handleNewChat = useCallback(() => {
    // If multiple agents are available, show the agent selector modal
    if (availableAgents && availableAgents.length > 1) {
      setNewChatModalOpen(true);
    } else {
      // Single agent mode - just start a new chat
      clearConversation();
      setShowLandingPage(true);
      setGoalInput('');
    }
  }, [clearConversation, availableAgents]);

  // Handle agent selection from new chat modal
  const handleAgentSelect = useCallback((agent: AgentInfo) => {
    setNewChatModalOpen(false);
    clearConversation();
    setShowLandingPage(true);
    setGoalInput('');
    // Notify parent about agent change
    if (onAgentChange) {
      onAgentChange(agent);
    }
  }, [clearConversation, onAgentChange]);

  // Handle title edit
  const handleEditTitle = useCallback(async (convId: string, newTitle: string) => {
    try {
      await updateConversationTitle(convId, newTitle);
      await loadConversations(undefined, agentId);
    } catch (error) {
      console.error('Failed to update title:', error);
    }
  }, [updateConversationTitle, loadConversations, agentId]);

  // Handle conversation delete
  const handleDeleteConversation = useCallback(async (convId: string) => {
    try {
      await deleteConversation(convId);
      if (conversationId === convId) {
        clearConversation();
        setShowLandingPage(true);
      }
      await loadConversations(undefined, agentId);
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  }, [deleteConversation, conversationId, clearConversation, loadConversations, agentId]);

  // Handle share
  const handleShare = useCallback((emails: string[]) => {
    if (onShare && conversationId) {
      onShare(emails, conversationId);
    }
    setShareModalOpen(false);
  }, [onShare, conversationId]);

  // Handle download — clone chat into new window for full-content PDF save
  const handleDownload = useCallback(() => {
    // If external handler provided, use it
    if (onDownload && conversationId) {
      onDownload(conversationId);
      return;
    }

    // Find the chat viewport
    const viewport = document.querySelector('[data-chat-viewport]') as HTMLElement;
    if (!viewport) return;

    // Open a new window
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Copy all stylesheets from the current page
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map(el => el.outerHTML)
      .join('\n');

    // Clone the full chat content (entire scroll height, not just viewport)
    const chatContent = viewport.innerHTML;

    const title = `${agentName || 'Chat'} — ${new Date().toLocaleDateString()}`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        ${styles}
        <style>
          html, body {
            margin: 0;
            padding: 0;
            background: #0a0a0a;
            color: #e5e5e5;
            font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
            font-size: 14px;
          }
          body {
            padding: 24px;
            max-width: 900px;
            margin: 0 auto;
          }
          /* Header */
          .print-header {
            border-bottom: 1px solid #333;
            padding-bottom: 16px;
            margin-bottom: 24px;
          }
          .print-header h1 {
            font-size: 18px;
            color: #fff;
            margin: 0 0 4px 0;
          }
          .print-header p {
            font-size: 12px;
            color: #888;
            margin: 0;
          }
          /* Ensure all content is visible */
          * {
            overflow: visible !important;
            max-height: none !important;
          }
          /* Code blocks */
          pre, code {
            white-space: pre-wrap !important;
            word-wrap: break-word !important;
          }
          pre {
            background: #1a1a1a !important;
            border: 1px solid #333 !important;
            border-radius: 6px !important;
            padding: 12px !important;
          }
          /* Print settings */
          @media print {
            body { padding: 12px; }
            .print-header { page-break-after: avoid; }
            pre { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <h1>${agentIcon ? agentIcon + ' ' : ''}${agentName || 'Chat Session'}</h1>
          <p>${conversationId || ''} · ${new Date().toLocaleString()}</p>
        </div>
        <div class="chat-content">
          ${chatContent}
        </div>
        <script>
          // Auto-trigger print after styles load, user can "Save as PDF"
          setTimeout(() => window.print(), 500);
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  }, [onDownload, conversationId, agentName, agentIcon]);

  const chatKey = conversationId || 'new';

  return (
    <div className={cn("eureka-chat flex flex-col h-full w-full max-w-full bg-background text-foreground overflow-hidden relative", className)}>
      {/* Top Header */}
      <header
        className={cn(
          "flex h-10 shrink-0 items-center gap-2 border-b border-border bg-card/95 backdrop-blur-sm px-3 transition-all duration-200 z-50",
          headerVisible ? "translate-y-0" : "-translate-y-full opacity-0 pointer-events-none"
        )}
      >
        {onBack && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onBack();
              window.dispatchEvent(new CustomEvent('eureka-chat-close'));
            }}
            className="inline-flex items-center gap-1 px-2 py-1 -ml-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors"
          >
            <ArrowLeftIcon className="w-3.5 h-3.5" />
            <span>Back</span>
          </button>
        )}

        <div className="flex items-center gap-1.5">
          {agentIcon && <span className="text-base">{agentIcon}</span>}
          <span className="text-sm font-medium text-foreground">{agentName}</span>
          {agentVersion && (
            <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {agentVersion}
            </span>
          )}
        </div>

        {/* Conversation info - ID and turns */}
        {conversationId && !showLandingPage && (
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <code className="rounded bg-muted px-1 py-0.5 font-mono">
              {conversationId.slice(0, 8)}
            </code>
            {executionCount > 0 && (
              <span className="rounded-full bg-emerald-900/50 px-1.5 py-0.5 text-emerald-400" title="Turns">
                {executionCount}
              </span>
            )}
          </div>
        )}

        <div className="ml-auto flex items-center gap-1">
          {/* Download button */}
          {conversationId && !showLandingPage && (
            <button
              onClick={handleDownload}
              className="group inline-flex items-center gap-1 px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
              title="Download Chat"
            >
              <DownloadIcon className="h-3.5 w-3.5" />
              <span className="text-xs hidden group-hover:inline">Download</span>
            </button>
          )}

          {/* Share button */}
          {conversationId && !showLandingPage && (
            <button
              onClick={() => setShareModalOpen(true)}
              className="group inline-flex items-center gap-1 px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
              title="Share Chat"
            >
              <Share2Icon className="h-3.5 w-3.5" />
              <span className="text-xs hidden group-hover:inline">Share</span>
            </button>
          )}

          {/* Agent List Toggle - only show when agents are available */}
          {(forceShowAgentList || isRouterAgent) && availableAgents && availableAgents.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActivePanel(activePanel === 'agents' ? null : 'agents')}
              className={cn(
                "h-7 w-7 p-0 text-muted-foreground hover:text-foreground",
                activePanel === 'agents' && "bg-muted text-foreground"
              )}
              title={activePanel === 'agents' ? "Hide agent catalog" : "Show agent catalog"}
            >
              <LayoutListIcon className="h-4 w-4" />
            </Button>
          )}

          {showHistory && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActivePanel(activePanel === 'history' ? null : 'history')}
              className={cn(
                "h-7 w-7 p-0 text-muted-foreground hover:text-foreground",
                activePanel === 'history' && "bg-muted text-foreground"
              )}
              title={activePanel === 'history' ? "Hide conversations" : "Show conversations"}
            >
              <MessageSquareIcon className="h-4 w-4" />
            </Button>
          )}

          <ThemeToggleSimple />
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden min-w-0 max-w-full">
        {/* Left Sidebar - History panel (when active) */}
        {showHistory && activePanel === 'history' && (
          <HistoryPanel
            conversations={conversations}
            activeConversationId={conversationId}
            showLandingPage={showLandingPage}
            onSelectConversation={handleContinueConversation}
            onNewChat={handleNewChat}
            onEditTitle={handleEditTitle}
            onDeleteConversation={handleDeleteConversation}
            agentName={agentName}
            agentVersion={agentVersion}
            onClose={() => setActivePanel(null)}
          />
        )}

        {/* Agent Catalog Panel - shows when activePanel === 'agents', works in both landing and chat views */}
        {activePanel === 'agents' && (forceShowAgentList || isRouterAgent) && availableAgents && availableAgents.length > 1 && (
          <AgentCatalogPanel
            agents={availableAgents}
            selectedAgentId={agentId || agentDefinition?.name || ''}
            onSelectAgent={(agent) => {
              if (onAgentChange) {
                onAgentChange(agent);
              }
              clearConversation();
              setShowLandingPage(true);
              setGoalInput('');
            }}
          />
        )}

        {/* Explorer View - takes full space when showing landing page */}
        {/* Show ExplorerView when no active conversation OR showLandingPage is true */}
        {(showLandingPage || !conversationId) ? (
          <ExplorerView
              apiBaseUrl={apiBaseUrl}
              tenantId={tenantId}
              userId={userId}
              currentAgent={{
                agent_id: agentId || agentDefinition?.name || '',
                name: agentName,
                display_name: agentName,
                icon: agentIcon,
                description: agentDescription,
                short_description: agentDescription,
                long_description: agentDescription,
                capabilities: agentCapabilities,
                usage_examples: agentExamples,
                mcp_servers: agentMcpServers,
                resource_binding_mode: agentResourceBindingMode,
              }}
              availableAgents={availableAgents?.map(a => ({
                agent_id: a.agent_id,
                execution_id: a.execution_id,
                name: a.name,
                display_name: a.name,
                icon: a.icon,
                short_description: a.short_description,
                long_description: a.long_description,
                category: a.category,
                version: a.version,
                capabilities: a.capabilities ?? undefined,
                usage_examples: a.usage_examples ?? undefined,
                // Pass basePath for file-based agents
                basePath: (a as any).basePath,
              }))}
              showAgentList={false}
              showMcpResources={true}
              enableAtCommands={enableAtCommands}
              searchCatalog={
                // For Developer Assistant, use skillCatalog (skills are the @ commands)
                skillCatalog && Object.keys(skillCatalog).length > 0
                  ? Object.entries(skillCatalog).map(([skillName]) => ({
                      id: skillName,
                      name: skillName,
                      displayName: skillName,
                      description: `Skill: ${skillName}`,
                      type: 'skill' as const,
                    }))
                  : availableAgents?.map(a => ({
                      id: a.agent_id,
                      name: a.name,
                      displayName: a.name,
                      description: a.short_description || a.long_description,
                      category: a.category,
                      tags: a.tags,
                      icon: a.icon,
                      type: 'agent' as const,
                    }))
              }
              onStart={({ agent, model, resource_bindings, goal }) => {
                setGoalInput(goal);
                // Store resource_bindings for passing to runtime
                // If agent changed, notify parent
                if (agent.agent_id !== agentId && onAgentChange) {
                  onAgentChange({
                    agent_id: agent.agent_id,
                    execution_id: agent.execution_id,
                    name: agent.name,
                    icon: agent.icon,
                    short_description: agent.short_description || agent.description,
                    capabilities: agent.capabilities,
                    basePath: agent.basePath,
                  });
                }
                // Pass goal directly to avoid stale state issue
                handleStartNewConversation(goal);
              }}
              onAgentChange={(agent) => {
                if (onAgentChange) {
                  onAgentChange({
                    agent_id: agent.agent_id,
                    execution_id: agent.execution_id,
                    name: agent.name,
                    icon: agent.icon,
                    short_description: agent.short_description || agent.description,
                    capabilities: agent.capabilities,
                    basePath: agent.basePath,
                  });
                }
              }}
            />
        ) : conversationId ? (
          /* Chat View - only when we have an active conversation */
          <div className="flex-1 min-w-0 max-w-full overflow-hidden flex flex-col">
            <ChatHeader
              conversation={currentConversation}
              agentName={agentName}
              agentVersion={agentVersion}
              onEditTitle={(newTitle) => handleEditTitle(conversationId, newTitle)}
              onViewDetails={() => setAgentDetailsModalOpen(true)}
            />
            <div className="flex-1 overflow-hidden">
              <ChatView
                key={chatKey}
                conversationId={conversationId}
                fetchMessages={fetchMessages}
                ensureConversation={ensureConversation}
                onExecutionStarted={addExecution}
                tenantId={tenantId}
                projectId={projectId}
                userId={userId}
                agentId={effectiveAgentId}
                basePath={basePath}
                skillCatalog={skillCatalog}
                workingDirectory={workingDirectory}
                environmentId={environmentId}
                persona={persona}
                configuration={configuration}
                agentWelcomeInfo={showWelcomeMessage ? agentWelcomeInfo : null}
                initialMessage={pendingInitialMessage || initialMessage}
                enableAtCommands={enableAtCommands}
                availableAgents={availableAgents}
              />
            </div>
          </div>
        ) : (
          /* No conversation - show empty state or custom render */
          <div className="flex-1 min-w-0 max-w-full overflow-hidden flex flex-col">
            {renderEmptyState
              ? renderEmptyState({ onNewChat: handleNewChat, onStartConversation: handleStartNewConversation })
              : <EmptyState onNewChat={handleNewChat} />}
          </div>
        )}
      </div>

      {/* Modals */}
      <ShareChatModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        onShare={handleShare}
        conversationTitle={currentConversation?.title}
      />

      <AgentDetailsModal
        open={agentDetailsModalOpen}
        onClose={() => setAgentDetailsModalOpen(false)}
        agentName={agentName}
        agentVersion={agentVersion}
        agentConfig={agentConfig}
        mcpServers={mcpServers}
      />

      {/* New Chat Modal - Agent Selector */}
      {availableAgents && availableAgents.length > 0 && (
        <NewChatModal
          open={newChatModalOpen}
          onClose={() => setNewChatModalOpen(false)}
          onSelectAgent={handleAgentSelect}
          agents={availableAgents}
        />
      )}
    </div>
  );
};

// ============================================================================
// Main Export
// ============================================================================

/**
 * AgentChatPage - Full-page chat component
 * Wraps content with ConversationProvider
 */
export const AgentChatPage = (props: AgentChatPageProps) => {
  const { tenantId, projectId, userId, ...contentProps } = props;
  return (
    <ThemeProvider>
      <ConversationProvider tenantId={tenantId} projectId={projectId} userId={userId}>
        <AgentChatContent {...contentProps} getToken={props.getToken} />
      </ConversationProvider>
    </ThemeProvider>
  );
};
